import type { Clip, Project, Track } from '@cinegrid/core';

export function formatTimecode(seconds: number, fps = 30): string {
  const sign = seconds < 0 ? '-' : '';
  const s = Math.max(0, Math.abs(seconds));
  const totalFrames = Math.round(s * fps);
  const ff = totalFrames % fps;
  const totalSeconds = Math.floor(totalFrames / fps);
  const ss = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const mm = totalMinutes % 60;
  const hh = Math.floor(totalMinutes / 60);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${sign}${pad(hh)}:${pad(mm)}:${pad(ss)}:${pad(ff)}`;
}

export function clipsOnTrack(project: Project, trackId: string): Clip[] {
  return project.clips
    .filter((c) => c.trackId === trackId)
    .sort((a, b) => a.start - b.start);
}

export interface TimelineGap {
  trackId: string;
  start: number;
  end: number;
}

/** Empty spans between clips on a track (and from 0 to first clip if any). */
export function findGapsOnTrack(project: Project, trackId: string): TimelineGap[] {
  const clips = clipsOnTrack(project, trackId);
  const gaps: TimelineGap[] = [];
  let cursor = 0;
  for (const c of clips) {
    if (c.start > cursor + 0.001) {
      gaps.push({ trackId, start: cursor, end: c.start });
    }
    cursor = Math.max(cursor, c.start + c.duration);
  }
  return gaps;
}

export function findGapAtTime(
  project: Project,
  trackId: string,
  time: number
): TimelineGap | null {
  return (
    findGapsOnTrack(project, trackId).find((g) => time >= g.start && time < g.end) ?? null
  );
}

/** Close a gap: pull all later clips on that track left by the gap length. */
export function closeGap(project: Project, gap: TimelineGap): Project {
  const len = gap.end - gap.start;
  if (len <= 0.001) return project;
  return {
    ...project,
    clips: project.clips.map((c) => {
      if (c.trackId !== gap.trackId) return c;
      if (c.start >= gap.end - 0.0001) {
        return { ...c, start: Math.max(0, c.start - len) };
      }
      return c;
    }),
    updatedAt: Date.now(),
  };
}

export function findClipAtTime(clips: Clip[], time: number): Clip | undefined {
  return clips.find((c) => time >= c.start && time < c.start + c.duration);
}

/** Split clip at absolute timeline time. Returns [left, right] or null if invalid. */
export function splitClip(clip: Clip, absoluteTime: number): [Clip, Clip] | null {
  if (absoluteTime <= clip.start + 0.001 || absoluteTime >= clip.start + clip.duration - 0.001) {
    return null;
  }
  const local = absoluteTime - clip.start;
  const left: Clip = {
    ...clip,
    id: crypto.randomUUID(),
    duration: local,
    outPoint: clip.inPoint + local,
  };
  const right: Clip = {
    ...clip,
    id: crypto.randomUUID(),
    start: absoluteTime,
    duration: clip.duration - local,
    inPoint: clip.inPoint + local,
  };
  return [left, right];
}

/** Ripple delete: remove clip and close the gap on that track. */
export function rippleDelete(project: Project, clipId: string): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  const end = clip.start + clip.duration;
  const remaining = project.clips.filter((c) => c.id !== clipId).map((c) => {
    if (c.trackId === clip.trackId && c.start >= end - 0.0001) {
      return { ...c, start: c.start - clip.duration };
    }
    return c;
  });
  return { ...project, clips: remaining, updatedAt: Date.now() };
}

/** Lift delete: remove clip, leave gap. */
export function liftDelete(project: Project, clipId: string): Project {
  return {
    ...project,
    clips: project.clips.filter((c) => c.id !== clipId),
    updatedAt: Date.now(),
  };
}

export function trimClip(
  project: Project,
  clipId: string,
  edge: 'in' | 'out',
  newAbsoluteTime: number,
  ripple: boolean,
  opts?: { minDuration?: number; maxSourceOut?: number }
): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip || clip.locked) return project;

  const minDur = opts?.minDuration ?? 1 / 30;
  let next = { ...clip };
  const oldStart = clip.start;
  const oldEnd = clip.start + clip.duration;
  const oldDuration = clip.duration;

  if (edge === 'in') {
    let clamped = Math.min(Math.max(newAbsoluteTime, 0), oldEnd - minDur);
    let delta = clamped - oldStart;
    // Don't trim past source start
    if (clip.inPoint + delta < 0) {
      delta = -clip.inPoint;
      clamped = oldStart + delta;
    }
    next = {
      ...next,
      start: clamped,
      duration: oldDuration - delta,
      inPoint: clip.inPoint + delta,
    };
    if (ripple) {
      const clips = project.clips.map((c) => {
        if (c.id === clipId) return next;
        if (c.trackId === clip.trackId && c.start >= oldStart - 0.0001) {
          return { ...c, start: Math.max(0, c.start + delta) };
        }
        return c;
      });
      return { ...project, clips, updatedAt: Date.now() };
    }
  } else {
    let clamped = Math.max(newAbsoluteTime, oldStart + minDur);
    let newDur = clamped - oldStart;
    let newOut = clip.inPoint + newDur;
    if (opts?.maxSourceOut != null && newOut > opts.maxSourceOut) {
      newOut = opts.maxSourceOut;
      newDur = Math.max(minDur, newOut - clip.inPoint);
      clamped = oldStart + newDur;
    }
    next = {
      ...next,
      duration: newDur,
      outPoint: newOut,
    };
    if (ripple) {
      const delta = newDur - oldDuration;
      const clips = project.clips.map((c) => {
        if (c.id === clipId) return next;
        if (c.trackId === clip.trackId && c.start >= oldEnd - 0.0001) {
          return { ...c, start: Math.max(0, c.start + delta) };
        }
        return c;
      });
      return { ...project, clips, updatedAt: Date.now() };
    }
  }

  return {
    ...project,
    clips: project.clips.map((c) => (c.id === clipId ? next : c)),
    updatedAt: Date.now(),
  };
}

export function moveClip(
  project: Project,
  clipId: string,
  newStart: number,
  newTrackId?: string
): Project {
  return {
    ...project,
    clips: project.clips.map((c) => {
      if (c.id !== clipId) return c;
      return {
        ...c,
        start: Math.max(0, newStart),
        trackId: newTrackId ?? c.trackId,
      };
    }),
    updatedAt: Date.now(),
  };
}

export function projectDuration(project: Project): number {
  const maxClip = project.clips.reduce((m, c) => Math.max(m, c.start + c.duration), 0);
  return Math.max(project.settings.duration, maxClip, 10);
}

/** All clip ids in the same A/V link group (includes self). */
export function linkedClipIds(project: Project, clipId: string): string[] {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip?.linkGroupId) return [clipId];
  return project.clips.filter((c) => c.linkGroupId === clip.linkGroupId).map((c) => c.id);
}

/** Expand a list of ids to include every linked partner. */
export function expandWithLinked(project: Project, ids: string[]): string[] {
  const out = new Set<string>();
  for (const id of ids) {
    for (const x of linkedClipIds(project, id)) out.add(x);
  }
  return [...out];
}

export function ensureTrack(project: Project, kind: Track['kind']): Track {
  const existing = project.tracks.find((t) => t.kind === kind);
  if (existing) return existing;
  return {
    id: crypto.randomUUID(),
    name: kind === 'video' ? 'V1' : kind === 'audio' ? 'A1' : 'S1',
    kind,
    height: kind === 'audio' ? 40 : 56,
    muted: false,
    solo: false,
    locked: false,
    visible: true,
  };
}
